import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import { Bank } from "@phosphor-icons/react/Bank";
import { PaperPlaneRight } from "@phosphor-icons/react/PaperPlaneRight";
import { Tag } from "@phosphor-icons/react/Tag";
import { ProductJsonLd } from "next-seo";
import * as React from "react";

import { Plan } from "./plan";
import { contactV1Or2 } from "config/paths";
import { headingFontFamily } from "../heading-font";

const plans = [
	{
		id: "business",
		name: "Business",
		popular: false,
		annualSavings: 238,
		priceAnnual: 79,
		priceMonthly: 99,
		currency: "USD",
		description:
			"For GIS pros and small teams publishing finished maps to the people who need them.",
		features: [
			"20 maps, 4 GB storage",
			"Unlimited public viewers",
			"Native support for Shapefile, GeoJSON, GeoTIFF, KML, CSV, MapInfo TAB, File Geodatabase, and WMS",
			"GIS tools built in: spatial query, proximity analysis, advanced search, reporting, and more",
			"Custom branding — logo, colours, domain",
			"Standard email support",
		],
	},
	{
		id: "enterprise",
		name: "Enterprise",
		popular: true,
		annualSavings: 478,
		priceAnnual: 159,
		priceMonthly: 199,
		action: <Button variant="contained">Start free trial</Button>,
		currency: "USD",
		description:
			"For GIS leads managing access, permissions, and data updates across a wider team.",
		features: [
			"40 maps, 8 GB+ storage",
			"Free private viewers",
			"Group access control with audit logs",
			"Data sync via Dropbox",
			"Remove MangoMap branding entirely",
			"Priority support & dedicated account manager",
			"Add editors, admins, storage, and maps as needed",
			"All Business features included",
		],
	},
	{
		id: "agency",
		name: "Agency",
		popular: false,
		annualSavings: 958,
		priceAnnual: 319,
		priceMonthly: 399,
		currency: "USD",
		description:
			"For agencies running multiple client projects at once, each with its own data and stakeholders.",
		features: [
			"100 maps, 16 GB+ storage",
			"Unlimited free private viewers",
			"Phone support & success manager",
			"Custom onboarding & team training",
			"Access to professional services",
			"All Enterprise features included",
		],
	},
];

const coreAddons = {
	monthly: {
		admins: 49,
		editors: 99,
		maps: {
			blockPrice: 25,
			blockSize: 5,
		},
		storage: {
			blockPrice: 50,
			blockSize: 50,
		},
	},
	annual: {
		admins: 39,
		editors: 79,
		maps: {
			blockPrice: 20,
			blockSize: 5,
		},
		storage: {
			blockPrice: 40,
			blockSize: 50,
		},
	},
};

function formatWholeDollarPrice(value: number) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	}).format(value);
}

export function PlansTable() {
	const [isAnnual, setIsAnnual] = React.useState(true);

	const handleToggle = () => {
		setIsAnnual((prev) => !prev);
	};

	const addonPricing = isAnnual ? coreAddons.annual : coreAddons.monthly;
	const addonsTitle = `Core add-ons billed ${isAnnual ? "annually" : "monthly"}`;
	const addonsCopy = isAnnual
		? [
				`Editors: 5 additional editor seats for ${formatWholeDollarPrice(addonPricing.editors)}/mo.`,
				`Admins: 1 additional admin seat for ${formatWholeDollarPrice(addonPricing.admins)}/mo.`,
				`Storage: ${addonPricing.storage.blockSize} GB additional storage for ${formatWholeDollarPrice(addonPricing.storage.blockPrice)}/mo.`,
				`Maps: ${addonPricing.maps.blockSize} additional maps for ${formatWholeDollarPrice(addonPricing.maps.blockPrice)}/mo.`,
			]
		: [
				`Editors: 5 additional editor seats for ${formatWholeDollarPrice(addonPricing.editors)}/mo.`,
				`Admins: 1 additional admin seat for ${formatWholeDollarPrice(addonPricing.admins)}/mo.`,
				`Storage: ${addonPricing.storage.blockSize} GB additional storage for ${formatWholeDollarPrice(addonPricing.storage.blockPrice)}/mo.`,
				`Maps: ${addonPricing.maps.blockSize} additional maps for ${formatWholeDollarPrice(addonPricing.maps.blockPrice)}/mo.`,
			];

	return (
		<Box
			sx={{
				pb: { xs: "60px", sm: "120px" },
				pt: {
					xs: "calc(60px + var(--MainNav-height))",
					sm: "calc(120px + var(--MainNav-height))",
				},
			}}>
			<ProductJsonLd
				useAppDir={false}
				productName="Mango"
				description="Simple, self-serve online GIS: publish interactive, branded web maps with fixed, all-inclusive pricing and unlimited public viewers."
				aggregateOffer={{
					priceCurrency: "USD",
					lowPrice: String(Math.min(...plans.map((plan) => plan.priceAnnual))),
					highPrice: String(
						Math.max(...plans.map((plan) => plan.priceMonthly)),
					),
					offerCount: String(plans.length),
				}}
			/>
			<Container maxWidth="lg">
				<Stack
					marginBottom={12}
					spacing={4}>
					<Stack
						spacing={2}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
						<Typography
							variant="h1"
							sx={{
								fontFamily: headingFontFamily,
								fontSize: { xs: "3rem", sm: undefined },
								lineHeight: { xs: 1.1, sm: undefined },
								textAlign: { xs: "left", sm: "center" },
							}}>
							Sensibly priced web maps, no claptrap.
						</Typography>
						<Typography
							color="text.secondary"
							sx={{
								textAlign: { xs: "left", sm: "center" },
								fontSize: { xs: "1.25rem", sm: undefined },
							}}
							variant="body1">
							Only pay for what you need. Scale when you&rsquo;re ready.
						</Typography>
						<Stack
							direction="row"
							spacing={2}
							sx={{
								alignSelf: { xs: "flex-start", sm: "center" },
								alignItems: "center",
								justifyContent: "flex-start",
							}}>
							<Switch
								color="success"
								checked={isAnnual}
								onChange={handleToggle}
								size="small"
							/>
							<Typography
								color="text.secondary"
								variant="overline">
								Billed annually
							</Typography>
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-start",
									minWidth: "72px",
								}}>
								{isAnnual ? (
									<Chip
										color="success"
										label="20% OFF"
										size="small"
									/>
								) : null}
							</Box>
						</Stack>
					</Stack>
					<div>
						<Grid
							container
							spacing={3}>
							{plans.map((plan) => (
								<Grid
									key={plan.id}
									size={{
										md: 4,
										xs: 12,
									}}>
									<Plan
										{...plan}
										action={plan.action}
										isAnnual={isAnnual}
										price={isAnnual ? plan.priceAnnual : plan.priceMonthly}
									/>
								</Grid>
							))}
						</Grid>
					</div>
					<Stack
						spacing={1.5}
						sx={{ alignItems: "center", textAlign: "center" }}>
						<Typography
							sx={{ fontFamily: headingFontFamily }}
							variant="h5">
							{addonsTitle}
						</Typography>
						<Stack spacing={0.5}>
							{addonsCopy.map((line) => (
								<Typography
									color="text.secondary"
									key={line}
									variant="body1">
									{line}
								</Typography>
							))}
						</Stack>
					</Stack>
				</Stack>
			</Container>
			<Container maxWidth="md">
				<Stack spacing={3}>
					<Typography
						align="center"
						sx={{ fontFamily: headingFontFamily }}
						variant="h5">
						Procurement and payment
					</Typography>
					<Grid
						container
						spacing={3}>
						<Grid size={{ md: 4, xs: 12 }}>
							<Card
								raised={true}
								sx={{ height: "100%" }}>
								<CardContent sx={{ p: 4 }}>
									<Stack spacing={2}>
										<Box width="36px">
											<Tag
												size="large"
												weight="duotone"
											/>
										</Box>
										<Typography
											color="text.secondary"
											component="p">
											Multi-year subscriptions get 30% off, giving teams
											predictable pricing for longer commitments.
										</Typography>
									</Stack>
								</CardContent>
							</Card>
						</Grid>
						<Grid size={{ md: 4, xs: 12 }}>
							<Card
								raised={true}
								sx={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
								}}>
								<CardContent sx={{ flexGrow: 1, p: 4 }}>
									<Stack spacing={2}>
										<Box width="36px">
											<Bank
												size="large"
												weight="duotone"
											/>
										</Box>
										<Typography
											color="text.secondary"
											component="p">
											Invoiced billing is available for annual and multi-year
											subscriptions.
										</Typography>
									</Stack>
								</CardContent>
								<CardActions
									sx={{ display: "flex", justifyContent: "flex-end", px: 4 }}>
									<Button
										color="secondary"
										endIcon={<PaperPlaneRight />}
										href={contactV1Or2}
										size="small"
										variant="outlined">
										Talk to sales
									</Button>
								</CardActions>
							</Card>
						</Grid>
						<Grid size={{ md: 4, xs: 12 }}>
							<Card
								raised={true}
								sx={{ height: "100%" }}>
								<CardContent sx={{ p: 4 }}>
									<Stack
										spacing={2}
										sx={{ height: "100%" }}>
										<Stack
											direction="row"
											spacing={2}
											sx={{
												alignItems: "center",
												justifyContent: "flex-start",
											}}>
											<Box
												component="img"
												src="/app-assets/payment-visa.svg"
												width="56px"
											/>
											<Box
												component="img"
												height="36px"
												src="/app-assets/payment-mastercard.svg"
											/>{" "}
											<Box
												component="img"
												height="42px"
												src="/app-assets/payment-amex.svg"
											/>
										</Stack>
										<Typography
											color="text.secondary"
											component="p">
											We accept VISA, Mastercard, and American Express cards for
											self-serve checkout.
										</Typography>
									</Stack>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</Stack>
			</Container>
		</Box>
	);
}
